<template>
  <md-dialog :md-active.sync="showDialog">
    <!-- Loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <md-progress-spinner md-mode="indeterminate"></md-progress-spinner>
    </div>

    <!-- Modal content with disabled state -->
    <div :class="{ 'modal-disabled': isLoading }">
      <md-dialog-title>
        <div class="title-spacing">{{ editItem.trip_id ? `${options.Lang?.passolution?.edit || 'Edit Item Trip ID: '} ` + editItem.trip_id : `${options.LangFile?.passolution?.edit_no_id || 'Edit Item'}`}}</div>
      </md-dialog-title>
      <md-dialog-content>
        <div class="form-fields">
          <md-field class="field-spacing">
            <label>{{ options.Lang.Name || "Name"}}</label>
            <md-input v-model="editItem.name" placeholder="name" :disabled="isLoading" disabled></md-input>
          </md-field>
          <md-field class="field-spacing">
            <label>Url</label>
            <md-input v-model="editItem.link" placeholder="Link" :disabled="isLoading" disabled></md-input>
          </md-field>
          <a :href="editItem.link" target="_blank">{{ options.Lang?.passolution?.open_link || 'Open Url' }}</a>

          <div class="params-section">
            <h3>{{ options.Lang?.passolution?.parameters || 'Parameters'}}</h3>

            <!-- Language Field -->
            <div v-if="paramsObject.query && paramsObject.query.lang !== undefined" class="language-section">
              <label class="language-label">Language</label>
              <input
                type="text"
                class="language-input"
                v-model="paramsObject.query.lang"
                placeholder="Language"
              />
            </div>

            <!-- Countries Field -->
            <div v-if="paramsObject.query && paramsObject.query.countries !== undefined" class="countries-section">
              <label class="countries-label">Countries</label>

              <!-- Display existing countries as separate dropdowns -->
              <div
                v-for="(countryObj, index) in getCountriesArray()"
                :key="`country-${index}`"
                class="country-item"
              >
                <div class="country-field">
                  <label class="country-item-label">Country {{ index + 1 }}</label>
                  <!-- Show loading spinner while countries are being fetched -->
                  <div v-if="isLoadingCountries" class="country-loading">
                    <md-progress-spinner md-mode="indeterminate" :md-diameter="20" :md-stroke="2"></md-progress-spinner>
                    <span class="loading-text">Loading countries...</span>
                  </div>
                  <!-- Custom autocomplete dropdown for countries -->
                  <div v-else class="custom-dropdown-container">
                    <div class="custom-dropdown" :class="{ 'dropdown-open': countryDropdownStates[index] }">
                      <input
                        type="text"
                        class="dropdown-input"
                        :value="countrySearchTerms[index] || getCountryDisplayValue(countryObj)"
                        @input="handleCountrySearch(index, $event.target.value)"
                        @focus="openCountryDropdown(index)"
                        @blur="closeCountryDropdown(index)"
                        :placeholder="'Search country...'"
                      />
                      <div class="dropdown-arrow">
                        <svg width="12" height="8" viewBox="0 0 12 8">
                          <path d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="2" fill="none"/>
                        </svg>
                      </div>
                      <div v-if="countryDropdownStates[index]" class="dropdown-options">
                        <div
                          v-for="country in getFilteredCountriesForField(index)"
                          :key="country.code || country.iso"
                          class="dropdown-option"
                          @mousedown="selectCountry(index, country)"
                        >
                          {{ country.name }} - {{ country.code || country.iso }}
                        </div>
                        <div v-if="getFilteredCountriesForField(index).length === 0" class="dropdown-option no-results">
                          No matching countries found
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="remove-country-btn"
                      @click="removeCountry(index)"
                      title="Remove country"
                    >
                      ×
                    </button>
                  </div>

                  <!-- Transit switch -->
                  <div class="transit-switch" style="margin-top:8px; display:flex; align-items:center; gap:8px;">
                    <label style="font-size:13px; color:#495057;">Transit</label>
                    <input
                      type="checkbox"
                      :checked="isCountryTransit(index)"
                      @change="toggleTransit(index, $event.target.checked)"
                      :disabled="isLoading"
                    />
                  </div>

                </div>
              </div>

              <!-- Add new country button -->
              <button
                type="button"
                class="add-country-btn"
                @click="addNewCountry"
                title="Add country"
              >
                +
              </button>
            </div>

            <!-- Travelers Section -->
            <div v-if="paramsObject.travelers && paramsObject.travelers.length > 0" class="travelers-section">
              <label class="travelers-label">Travelers</label>
              <div
                v-for="(traveler, index) in paramsObject.travelers"
                :key="index"
                class="traveler-item"
              >
                <span class="traveler-name">
                  {{ traveler.firstName && traveler.lastName ? `${traveler.firstName} ${traveler.lastName}` : `Traveler ${index + 1}` }}
                </span>
                <div class="nationality-field">
                  <label class="nationality-label">Nationality</label>
                  <!-- Show loading spinner while countries are being fetched -->
                  <div v-if="isLoadingCountries" class="nationality-loading">
                    <md-progress-spinner md-mode="indeterminate" :md-diameter="20" :md-stroke="2"></md-progress-spinner>
                    <span class="loading-text">Loading countries...</span>
                  </div>
                  <!-- Custom autocomplete dropdown -->
                  <div v-else class="custom-dropdown-container">
                    <div class="custom-dropdown" :class="{ 'dropdown-open': dropdownStates[index] }">
                      <input
                        type="text"
                        class="dropdown-input"
                        :value="searchTerms[index] || getDisplayValue(traveler.nationalityCode)"
                        @input="handleSearch(index, $event.target.value)"
                        @focus="openDropdown(index)"
                        @blur="closeDropdown(index)"
                        :placeholder="'Search nationality...'"
                      />
                      <div class="dropdown-arrow">
                        <svg width="12" height="8" viewBox="0 0 12 8">
                          <path d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="2" fill="none"/>
                        </svg>
                      </div>
                      <div v-if="dropdownStates[index]" class="dropdown-options">
                        <div
                          v-for="country in getFilteredCountries(index)"
                          :key="country.code || country.iso"
                          class="dropdown-option"
                          @mousedown="selectNationality(index, country)"
                        >
                          {{ country.name }} - {{ country.code || country.iso }}
                        </div>
                        <div v-if="getFilteredCountries(index).length === 0" class="dropdown-option no-results">
                          No matching countries found
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Trip Parameters -->
            <div v-if="paramsObject.query && paramsObject.query.trip" class="trip-section">
              <label class="trip-label">Trip</label>

              <div class="trip-field">
                <label class="trip-field-label">Start Date</label>
                <input
                  type="text"
                  class="trip-input"
                  v-model="paramsObject.query.trip.start_date"
                  placeholder="Start Date"
                />
              </div>

              <div class="trip-field">
                <label class="trip-field-label">End Date</label>
                <input
                  type="text"
                  class="trip-input"
                  v-model="paramsObject.query.trip.end_date"
                  placeholder="End Date"
                />
              </div>

              <div class="trip-field">
                <label class="trip-field-label">Trip Name</label>
                <input
                  type="text"
                  class="trip-input"
                  v-model="paramsObject.query.trip.name"
                  placeholder="Trip Name"
                />
              </div>
            </div>
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions class="centered-actions">
        <md-button class="md-raised secondary" @click="cancel" :disabled="isLoading">{{ options.Lang?.Abbrechen || 'Cancel'}}</md-button>
        <md-button class="md-primary md-raised" @click="recreate" :disabled="isLoading">
          {{ options.Lang?.passolution?.recreat || 'Recreate'}}
        </md-button>
      </md-dialog-actions>
    </div>
  </md-dialog>
</template>

<script>
export default {
  name: 'YourEditModal',
  props: {
    options: Object,
    currentItem: Object
  },
  data() {
    return {
      editItem: {},
      paramsObject: {},
      isLoading: false,
      countries: [],
      isLoadingCountries: true,
      dropdownStates: {},
      searchTerms: {},
      countryDropdownStates: {},
      countrySearchTerms: {}
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.options.showEditPassolutionForm;
      },
      set(value) {
        this.options.showEditPassolutionForm = value;
      }
    }
  },
  mounted() {
    this.fetchCountries();
  },
  watch: {
    currentItem: {
      handler(newItem) {
        if (newItem) {
          this.editItem = { ...newItem };
          if (typeof newItem.params === 'string') {
            try {
              this.paramsObject = JSON.parse(newItem.params);
            } catch (e) {
              this.paramsObject = {};
            }
          } else if (typeof newItem.params === 'object' && newItem.params !== null) {
            this.paramsObject = JSON.parse(JSON.stringify(newItem.params));
          } else {
            this.paramsObject = {};
          }

          // Initialize dropdown states for travelers
          if (this.paramsObject.travelers) {
            this.dropdownStates = {};
            this.searchTerms = {};
            this.paramsObject.travelers.forEach((_, index) => {
              this.$set(this.dropdownStates, index, false);
              this.$set(this.searchTerms, index, '');
            });
          }

          // Initialize dropdown states for countries
          if (this.paramsObject.query && this.paramsObject.query.countries !== undefined) {
            // Normalize countries into array of objects: { destination: 'SG', type?: 'transit' }
            const rawCountries = this.paramsObject.query.countries;
            const normalized = [];

            if (typeof rawCountries === 'string') {
              // "SG,AU,DE"
              rawCountries
                .split(',')
                .map(c => c.trim())
                .filter(Boolean)
                .forEach(code => normalized.push({ destination: code }));

            } else if (Array.isArray(rawCountries)) {
              rawCountries.forEach(item => {
                if (item && typeof item === 'object') {
                  // Keep destination and type if present
                  const dest = item.destination || item.destination_code || item.code || item.iso || '';
                  const type = item.type;
                  if (dest) normalized.push({ destination: dest, type });
                } else if (typeof item === 'string') {
                  normalized.push({ destination: item.trim() });
                }
              });

            } else if (rawCountries && typeof rawCountries === 'object') {
              // {0:'SG',1:'AU'} style
              Object.values(rawCountries)
                .map(v => (typeof v === 'string' ? v.trim() : ''))
                .filter(Boolean)
                .forEach(code => normalized.push({ destination: code }));
            }

            // Ensure it's an array
            if (!Array.isArray(normalized)) {
              this.paramsObject.query.countries = [];
            } else {
              this.paramsObject.query.countries = normalized;
            }

            // Initialize dropdown states for each country (now objects)
            this.countryDropdownStates = {};
            this.countrySearchTerms = {};
            this.paramsObject.query.countries.forEach((_, index) => {
              this.$set(this.countryDropdownStates, index, false);
              this.$set(this.countrySearchTerms, index, '');
            });

            // Add at least one empty country field if none exist
            if (this.paramsObject.query.countries.length === 0) {
              this.paramsObject.query.countries.push({ destination: '' });
              this.$set(this.countryDropdownStates, 0, false);
              this.$set(this.countrySearchTerms, 0, '');
            }
          }

          // Force Vue to make the object reactive
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      },
      immediate: true
    },
    // Watch for changes in paramsObject to ensure reactivity
    paramsObject: {
      handler(newVal) {
        // This ensures all nested properties remain reactive
        this.$forceUpdate();
      },
      deep: true
    }
  },
  methods: {
    cancel() {
      this.options.showEditPassolutionForm = false;
      this.editItem = {};
      this.paramsObject = {};
      this.dropdownStates = {};
      this.searchTerms = {};
    },
    async recreate() {
      let self = this;
      self.editItem.params = JSON.parse(JSON.stringify(self.paramsObject));
      self.isLoading = true;
      await this.doPost({
        userdata: self.options.user,
        data: self.editItem
      }, 'passolution/updateEntry', function (res) {
        self.isLoading = false;
        if (res.data.error || res.data.status === 400) {
          if (res.data?.message){
            self.options.errorMsg = self.$t(res.data.message);
            self.options.showError = true;
          }
          self.help.log(self.$t(res.msg), false, true);
        } else {
          self.options.showSuccess = true;
          self.options.showEditPassolutionForm = false;
          self.$emit('updateData');
        }
      });
    },
    async fetchCountries() {
      this.isLoadingCountries = true;
      try {
        await this.doPost({}, 'passolution/countries', (res) => {
          if (res && res.data && res.data.data && res.data.data.countries) {
            // Handle object format - convert to array for dropdown
            if (typeof res.data.data.countries === 'object' && !Array.isArray(res.data.data.countries)) {
              // Convert object to array format expected by dropdown
              this.countries = Object.keys(res.data.data.countries).map(key => ({
                code: key,
                name: res.data.data.countries[key]
              }));
            } else if (Array.isArray(res.data.data.countries)) {
              this.countries = res.data.data.countries;
            } else {
              this.countries = [];
            }
          } else {
            this.countries = [];
          }
          this.isLoadingCountries = false;
        }, 1);
      } catch (error) {
        console.error('Error fetching countries:', error);
        this.countries = [];
        this.isLoadingCountries = false;
      }
    },
    updateTravelerNationality(travelerIndex, value) {
      // Update the specific traveler's nationality in paramsObject
      if (this.paramsObject.travelers && this.paramsObject.travelers[travelerIndex]) {
        this.$set(this.paramsObject.travelers[travelerIndex], 'nationalityCode', value);
      }

      // Update the nat field with comma-separated nationality codes
      if (this.paramsObject.travelers && this.paramsObject.query) {
        const nationalities = this.paramsObject.travelers
          .map(t => t.nationalityCode)
          .filter(nat => nat)
          .join(',');
        this.paramsObject.query.nat = nationalities;
      }
    },
    handleSearch(index, searchTerm) {
      // Handle search input for autocomplete
      this.$set(this.searchTerms, index, searchTerm);
      this.$set(this.dropdownStates, index, true);

      // If user starts typing, clear the selected nationality so they can search fresh
      if (searchTerm && this.paramsObject.travelers[index]) {
        // Only clear if the search term doesn't match the current display value
        const currentDisplay = this.getDisplayValue(this.paramsObject.travelers[index].nationalityCode);
        if (searchTerm !== currentDisplay) {
          this.$set(this.paramsObject.travelers[index], 'nationalityCode', '');
        }
      }

      // If user clears the search completely, also clear the selected nationality
      if (!searchTerm && this.paramsObject.travelers[index]) {
        this.$set(this.paramsObject.travelers[index], 'nationalityCode', '');
        this.updateTravelerNationality(index, '');
      }
    },
    openDropdown(index) {
      // Open the dropdown for the specific traveler
      this.$set(this.dropdownStates, index, true);
      // Clear search term when opening to show all countries
      if (!this.searchTerms[index]) {
        this.$set(this.searchTerms, index, '');
      }
    },
    closeDropdown(index) {
      // Close the dropdown for the specific traveler with delay to allow option click
      setTimeout(() => {
        this.$set(this.dropdownStates, index, false);
        // If no country is selected, clear the search term
        if (!this.paramsObject.travelers[index]?.nationalityCode) {
          this.$set(this.searchTerms, index, '');
        }
      }, 150);
    },
    getDisplayValue(nationalityCode) {
      // Get the display value for the input field
      if (!nationalityCode) return '';
      const country = this.countries.find(c => (c.code || c.iso) === nationalityCode);
      return country ? `${country.name} - ${country.code || country.iso}` : '';
    },
    getFilteredCountries(index) {
      // Get filtered countries based on search term
      const searchTerm = this.searchTerms[index]?.toLowerCase() || '';

      // If no search term, show all countries
      if (!searchTerm) return this.countries.slice(0, 50); // Limit to first 50 for performance

      return this.countries.filter(country => {
        const countryName = country.name.toLowerCase();
        const countryCode = (country.code || country.iso).toLowerCase();
        return countryName.includes(searchTerm) || countryCode.includes(searchTerm);
      }).sort((a, b) => {
        // Sort by relevance - exact matches first, then name matches, then code matches
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        const aCode = (a.code || a.iso).toLowerCase();
        const bCode = (b.code || b.iso).toLowerCase();

        const aNameExact = aName.startsWith(searchTerm);
        const bNameExact = bName.startsWith(searchTerm);
        const aCodeExact = aCode.startsWith(searchTerm);
        const bCodeExact = bCode.startsWith(searchTerm);

        if (aNameExact && !bNameExact) return -1;
        if (!aNameExact && bNameExact) return 1;
        if (aCodeExact && !bCodeExact) return -1;
        if (!aCodeExact && bCodeExact) return 1;

        return aName.localeCompare(bName);
      });
    },
    selectNationality(index, country) {
      // Select nationality from the dropdown
      const countryCode = country.code || country.iso;
      this.$set(this.paramsObject.travelers[index], 'nationalityCode', countryCode);
      this.$set(this.searchTerms, index, ''); // Clear search term after selection
      this.$set(this.dropdownStates, index, false);
      this.updateTravelerNationality(index, countryCode);
    },
    handleCountrySearch(index, searchTerm) {
      // Handle search input for country autocomplete
      this.$set(this.countrySearchTerms, index, searchTerm);
      this.$set(this.countryDropdownStates, index, true);

      // If user starts typing, clear the selected country so they can search fresh
      if (searchTerm && this.paramsObject.query.countries[index]) {
        // Only clear if the search term doesn't match the current display value
        const currentDisplay = this.getCountryDisplayValue(this.paramsObject.query.countries[index]);
        if (searchTerm !== currentDisplay) {
          this.$set(this.paramsObject.query.countries, index, '');
        }
      }

      // If user clears the search completely, also clear the selected country
      if (!searchTerm && this.paramsObject.query.countries[index]) {
        this.$set(this.paramsObject.query.countries, index, '');
      }
    },
    openCountryDropdown(index) {
      // Open the dropdown for the specific country field
      this.$set(this.countryDropdownStates, index, true);
      // Clear search term when opening to show all countries
      if (!this.countrySearchTerms[index]) {
        this.$set(this.countrySearchTerms, index, '');
      }
    },
    closeCountryDropdown(index) {
      // Close the dropdown for the specific country field with delay to allow option click
      setTimeout(() => {
        this.$set(this.countryDropdownStates, index, false);
        // If no country is selected, clear the search term
        if (!this.paramsObject.query.countries[index]) {
          this.$set(this.countrySearchTerms, index, '');
        }
      }, 150);
    },
    getCountryDisplayValue(countryCode) {
      // Get the display value for the country input field
      if (!countryCode) return '';
      // countryCode may be an object { destination, type } or a string
      const code = typeof countryCode === 'object' ? countryCode.destination : countryCode;
      if (!code) return '';
      const country = this.countries.find(c => (c.code || c.iso) === code);
      return country ? `${country.name} - ${country.code || country.iso}` : '';
    },
    getFilteredCountriesForField(index) {
      // Get filtered countries based on search term for the specific country field
      const searchTerm = this.countrySearchTerms[index]?.toLowerCase() || '';

      // If no search term, show all countries
      if (!searchTerm) return this.countries.slice(0, 50); // Limit to first 50 for performance

      return this.countries.filter(country => {
        const countryName = country.name.toLowerCase();
        const countryCode = (country.code || country.iso).toLowerCase();
        return countryName.includes(searchTerm) || countryCode.includes(searchTerm);
      }).sort((a, b) => {
        // Sort by relevance - exact matches first, then name matches, then code matches
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        const aCode = (a.code || a.iso).toLowerCase();
        const bCode = (b.code || b.iso).toLowerCase();

        const aNameExact = aName.startsWith(searchTerm);
        const bNameExact = bName.startsWith(searchTerm);
        const aCodeExact = aCode.startsWith(searchTerm);
        const bCodeExact = bCode.startsWith(searchTerm);

        if (aNameExact && !bNameExact) return -1;
        if (!aNameExact && bNameExact) return 1;
        if (aCodeExact && !bCodeExact) return -1;
        if (!aCodeExact && bCodeExact) return 1;

        return aName.localeCompare(bName);
      });
    },
    selectCountry(index, country) {
      // Select country from the dropdown
      const countryCode = country.code || country.iso;
      // Preserve existing 'type' if present
      const existing = this.paramsObject.query.countries[index] || {};
      this.$set(this.paramsObject.query.countries, index, { destination: countryCode, type: existing.type });
      this.$set(this.countrySearchTerms, index, ''); // Clear search term after selection
      this.$set(this.countryDropdownStates, index, false);
    },
    getCountriesArray() {
      // Get the countries array from paramsObject, ensuring it's an array of objects
      const countries = this.paramsObject.query && this.paramsObject.query.countries;
      if (Array.isArray(countries)) return countries;
      return [];
    },
    addNewCountry() {
      // Add a new empty country field
      this.paramsObject.query.countries.push({ destination: '' });
      const newIndex = this.paramsObject.query.countries.length - 1;
      this.$set(this.countryDropdownStates, newIndex, false);
      this.$set(this.countrySearchTerms, newIndex, '');
    },
    removeCountry(index) {
      // Remove the country at the specified index
      this.paramsObject.query.countries.splice(index, 1);
      this.$delete(this.countryDropdownStates, index);
      this.$delete(this.countrySearchTerms, index);
    },

    // Transit helpers
    isCountryTransit(index) {
      const item = this.paramsObject.query.countries && this.paramsObject.query.countries[index];
      return !!(item && item.type === 'transit');
    },
    toggleTransit(index, checked) {
      const item = this.paramsObject.query.countries && this.paramsObject.query.countries[index];
      if (!item) return;
      if (checked) {
        this.$set(this.paramsObject.query.countries[index], 'type', 'transit');
      } else {
        // remove the type property so it defaults to false
        if (this.paramsObject.query.countries[index].hasOwnProperty('type')) {
          this.$delete(this.paramsObject.query.countries[index], 'type');
        }
      }
    }
  }
};
</script>

<style scoped>
.form-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.title-spacing {
  padding-top: 24px;
  padding-left: 24px;
}
.field-spacing {
  margin-bottom: 16px;
}
.centered-actions {
  display: flex;
  justify-content: center;
  gap: 24px;
}
.params-section {
  margin-top: 24px;
  padding: 16px;
  background: #f7f7f7;
  border-radius: 8px;
}
.param-field {
  margin-bottom: 12px;
}
.travelers-section {
  margin-bottom: 24px;
}

.travelers-label {
  font-weight: bold;
  display: block;
  margin-bottom: 16px;
}

.traveler-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  padding: 20px;
  border: 1px solid #e1e5e9;
  border-radius: 12px;
  background-color: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.traveler-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #c1c7cd;
}

.traveler-name {
  min-width: 150px;
  margin-right: 16px;
  font-weight: 500;
}

.nationality-field {
  flex: 1;
  margin-bottom: 0;
}

.nationality-label {
  font-weight: 600;
  margin-bottom: 8px;
  display: block;
  color: #495057;
  font-size: 14px;
}

.custom-dropdown-container {
  position: relative;
  width: 100%;
}

.custom-dropdown {
  position: relative;
  width: 100%;
}

.dropdown-input {
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  font-size: 14px;
  background-color: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.dropdown-input:focus {
  border-color: #4285f4;
  box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
  outline: none;
}

.dropdown-input:hover {
  border-color: #c1c7cd;
}

.dropdown-arrow {
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  pointer-events: none;
  color: #6c757d;
  transition: transform 0.2s ease;
}

.dropdown-open .dropdown-arrow {
  transform: translateY(-50%) rotate(180deg);
}

.dropdown-options {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border: 1px solid #e1e5e9;
  border-radius: 12px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  animation: slideDown 0.2s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-option {
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f8f9fa;
  font-size: 14px;
}

.dropdown-option:last-child {
  border-bottom: none;
}

.dropdown-option:hover {
  background-color: #f8f9fa;
  color: #4285f4;
}

.dropdown-option:first-child {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.dropdown-option:last-child {
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

.no-results {
  color: #6c757d;
  text-align: center;
  padding: 16px;
  font-style: italic;
}

.nationality-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
}

.loading-text {
  font-size: 14px;
  color: #666;
}

.trip-section {
  margin-bottom: 24px;
}

.trip-label {
  font-weight: bold;
  display: block;
  margin-bottom: 16px;
}

.trip-field {
  margin-bottom: 16px;
}

.trip-field-label {
  font-weight: 600;
  margin-bottom: 8px;
  display: block;
  color: #495057;
  font-size: 14px;
}

.trip-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  font-size: 14px;
  background-color: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.trip-input:focus {
  border-color: #4285f4;
  box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
  outline: none;
}

.trip-input:hover {
  border-color: #c1c7cd;
}

.md-loading {
  animation: flip 1.5s ease-in-out infinite;
}
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 4px;
}

.modal-disabled {
  pointer-events: none;
  opacity: 0.6;
}

.md-dialog {
  max-height: 90vh;
  overflow-y: auto;
}

.md-dialog-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: 24px;
}

.md-dialog-actions {
  position: sticky;
  bottom: 0;
  background: white;
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
}
@keyframes flip {
  0% {
    transform: rotateX(0deg);
  }
  50% {
    transform: rotateX(180deg);
  }
  100% {
    transform: rotateX(360deg);
  }
}

.countries-section {
  margin-bottom: 24px;
}

.countries-label {
  font-weight: bold;
  display: block;
  margin-bottom: 16px;
}

.country-item {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #e1e5e9;
  border-radius: 12px;
  background-color: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.country-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #c1c7cd;
}

.country-field {
  flex: 1;
  margin-right: 16px;
}

.country-item-label {
  font-weight: 600;
  margin-bottom: 8px;
  display: block;
  color: #495057;
  font-size: 14px;
}

.remove-country-btn {
  background: none;
  border: none;
  color: #dc3545;
  font-size: 18px;
  cursor: pointer;
  transition: color 0.2s ease;
}

.remove-country-btn:hover {
  color: #c82333;
}

.add-country-btn {
  background: none;
  border: none;
  color: #000;
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 8px;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.add-country-btn:hover {
  background-color: #f0f0f0;
  color: #333;
}

.language-section {
  margin-bottom: 24px;
}

.language-label {
  font-weight: 600;
  margin-bottom: 8px;
  display: block;
  color: #495057;
  font-size: 14px;
}

.language-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  font-size: 14px;
  background-color: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.language-input:focus {
  border-color: #4285f4;
  box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
  outline: none;
}

.language-input:hover {
  border-color: #c1c7cd;
}
</style>
